import { useTranslation } from '@ice-web-app/shared-hooks';
import { useSidebarStore } from '@ice-web-app/shared-store';
import { SetupStepItem, SidebarMenuItem } from '@ice-web-app/shared-types';
import { translate } from '@ice-web-app/shared-utils-ssr';
import { Stack } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useEffect, useState } from 'react';
import PerfectScrollbar from 'react-perfect-scrollbar';
import { DsButton } from '../../../../components/buttons/DsButton';
import { DsIcon } from '../../../../components/icons/DsIcon';
import { DsStepper } from '../../../../components/stepper/DsStepper';
import { DsTypography } from '../../../../components/typography/DsTypography';
import menuItemStyles from '../../../@core/styles/vertical/menuItemStyles';
import menuSectionStyles from '../../../@core/styles/vertical/menuSectionStyles';
import type { VerticalMenuContextProps } from '../../../@menu/components/vertical-menu/Menu';
import useVerticalNav from '../../../@menu/hooks/useVerticalNav';
import StyledVerticalNavExpandIcon from '../../../@menu/styles/vertical/StyledVerticalNavExpandIcon';
import { Menu } from '../../../@menu/vertical-menu';
import VerticalMenuFooter from './VerticalMenuFooter';
import VerticalMenuItems from './VerticalMenuItems';
import VerticalMenuSubItems from './VerticalMenuSubItems';

type RenderExpandIconProps = {
  open?: boolean;
  transitionDuration?: VerticalMenuContextProps['transitionDuration'];
};

type Props = {
  scrollMenu: (container: any, isPerfectScrollbar: boolean) => void;
};

const RenderExpandIcon = ({ open, transitionDuration }: RenderExpandIconProps) => (
  <StyledVerticalNavExpandIcon open={open} transitionDuration={transitionDuration}>
    <DsIcon icon="CHEVRON_RIGHT" />
  </StyledVerticalNavExpandIcon>
);

export const VerticalMenu = ({ scrollMenu }: Props) => {
  const { t } = useTranslation('common');
  const { menuItems, setupStepItems, menuMySidebarItems, entityMenuItems, showEntityMenu } = useSidebarStore();
  const theme = useTheme();
  const verticalNavOptions = useVerticalNav();
  const { isBreakpointReached, transitionDuration } = verticalNavOptions;
  const [addSidebarMode, _setAddSidebarMode] = useState(false);
  const [selectedSidebar, setSelectedSidebar] = useState<SidebarMenuItem[]>([]);

  const toggleSelectSidebar = (menu: SidebarMenuItem) => {
    if (selectedSidebar.find((menuItem) => menuItem.path === menu.path)) {
      const newArray = selectedSidebar.filter((val) => val.path !== menu.path);
      setSelectedSidebar(newArray);
    } else {
      setSelectedSidebar([...selectedSidebar, { ...menu }]);
    }
  };

  useEffect(() => {
    setSelectedSidebar(menuMySidebarItems);
  }, []);

  const ScrollWrapper = isBreakpointReached ? 'div' : PerfectScrollbar;

  const menuSidebarItems = showEntityMenu
    ? entityMenuItems
    : localStorage.getItem('MY_SIDEBAR') === 'true'
      ? menuMySidebarItems
      : menuItems;
  return (
    // eslint-disable-next-line lines-around-comment
    /* Custom scrollbar instead of browser scroll, remove if you want browser scroll only */
    <>
      <ScrollWrapper
        {...(isBreakpointReached
          ? {
              className: 'bs-full overflow-y-auto overflow-x-hidden',
              onScroll: (container) => scrollMenu(container, false),
            }
          : {
              options: { wheelPropagation: false, suppressScrollX: true },
              onScrollY: (container) => scrollMenu(container, true),
            })}
      >
        {/* Incase you also want to scroll NavHeader to scroll with Vertical Menu, remove NavHeader from above and paste it below this comment */}
        {/* Vertical Menu */}
        <Menu
          popoutMenuOffset={{ mainAxis: 10 }}
          menuItemStyles={menuItemStyles(verticalNavOptions, theme)}
          renderExpandIcon={({ open }) => <RenderExpandIcon open={open} transitionDuration={transitionDuration} />}
          renderExpandedMenuItemIcon={{ icon: <DsIcon sx={{ display: 'none' }} icon="PLUS_CIRCLE_FILL" /> }}
          menuSectionStyles={menuSectionStyles(verticalNavOptions, theme)}
        >
          {/* {backPath && (
            <MenuItem
              style={{ color: theme.palette.primary.main }}
              href={backPath}
              icon={<DsIcon icon="ARROW_LEFT" size="md" />}
            >
              {backText || t('BACK')}
            </MenuItem>
          )} */}
          {localStorage.getItem('MY_SIDEBAR') === 'true' && (
            <DsTypography
              variant="body2"
              color="text.secondary"
              sx={{ paddingInlineStart: 5, paddingBottom: 2, paddingTop: 6 }}
            >
              {t('MY_SIDEBAR')}
            </DsTypography>
          )}

          {!!menuSidebarItems.length &&
            menuSidebarItems?.map((menu: SidebarMenuItem, menuIndex: number) => {
              return menu.submenu?.length ? (
                <VerticalMenuSubItems
                  key={menuIndex}
                  addSidebarMode={addSidebarMode}
                  menu={menu}
                  menuIndex={menuIndex}
                  selectedSidebar={selectedSidebar}
                  toggleSelectSidebar={toggleSelectSidebar}
                  setSelectedSidebar={setSelectedSidebar}
                />
              ) : (
                <VerticalMenuItems
                  key={menuIndex}
                  addSidebarMode={addSidebarMode}
                  menu={menu}
                  menuIndex={menuIndex}
                  selectedSidebar={selectedSidebar}
                  toggleSelectSidebar={toggleSelectSidebar}
                />
              );
            })}
        </Menu>
        {!!setupStepItems.length && (
          <Stack gap={4} px={5} mt={-3}>
            <DsButton
              variant="text"
              color="secondary"
              size="large"
              startIcon={<DsIcon icon="ARROW_LEFT" />}
              sx={{ width: 'fit-content', '&:hover': { backgroundColor: 'unset !important' } }}
            >
              {translate({ key: 'ADMIN_HUB', ns: 'common' })}
            </DsButton>
            <DsTypography variant="body2">{translate({ key: 'SETUP_PROCESS', ns: 'common' })}</DsTypography>
            <DsStepper
              steps={setupStepItems.map((step: SetupStepItem) => ({
                title: step.title,
                type: step.type,
                path: step.pathEndpoint,
              }))}
              orientation="vertical"
              withOutStepNum
            />
            {/* {setupStepItems.map((step: SetupStepItem, index: number) => {
              return (
                <MenuItem href={step.path} icon={step.icon} key={index}>
                {step.title}
                </MenuItem>
                );
                })} */}
          </Stack>
        )}

        {/* <Menu
        popoutMenuOffset={{ mainAxis: 10 }}
        menuItemStyles={menuItemStyles(verticalNavOptions, theme)}
        renderExpandIcon={({ open }) => <RenderExpandIcon open={open} transitionDuration={transitionDuration} />}
        renderExpandedMenuItemIcon={{ icon: <DsIcon customIcon={<LuPlusCircle /> }}
        menuSectionStyles={menuSectionStyles(verticalNavOptions, theme)}
        >
        <GenerateVerticalMenu menuData={menuData(dictionary)} />
        </Menu> */}
      </ScrollWrapper>
      {/* {!setupStepItems.length && (
        <VerticalMenuFooter
          addSidebarMode={addSidebarMode}
          selectedSidebar={selectedSidebar}
          setAddSidebarMode={setAddSidebarMode}
        />
      )} */}
    </>
  );
};
